/**
 * Language Detection Utility for Multi-keyword Search Enhancement
 * Detects languages in search queries to improve movie search results
 */

export const LANGUAGE_MAPPINGS: { [key: string]: string } = {
  // Indian Languages
  'malayalam': 'ml',
  'hindi': 'hi',
  'tamil': 'ta',
  'telugu': 'te',
  'kannada': 'kn',
  'marathi': 'mr',
  'bengali': 'bn',
  'gujarati': 'gu',
  'punjabi': 'pa',
  'urdu': 'ur',
  
  // European Languages
  'english': 'en',
  'spanish': 'es',
  'french': 'fr',
  'german': 'de',
  'italian': 'it',
  'portuguese': 'pt',
  'dutch': 'nl',
  'russian': 'ru',
  'polish': 'pl',
  'swedish': 'sv',
  'norwegian': 'no',
  'danish': 'da',
  'finnish': 'fi',
  
  // Asian Languages
  'japanese': 'ja',
  'korean': 'ko',
  'chinese': 'zh',
  'mandarin': 'zh',
  'cantonese': 'zh',
  'thai': 'th',
  'vietnamese': 'vi',
  'indonesian': 'id',
  'malay': 'ms',
  
  // Other Languages
  'arabic': 'ar',
  'hebrew': 'he',
  'turkish': 'tr',
  'greek': 'el',
  'hungarian': 'hu',
  'czech': 'cs',
  'slovak': 'sk',
  'romanian': 'ro',
  'bulgarian': 'bg',
  'croatian': 'hr',
  'serbian': 'sr',
  'ukrainian': 'uk',
  'lithuanian': 'lt',
  'latvian': 'lv',
  'estonian': 'et',
  'slovenian': 'sl',
  
  // Alternative names and regional variants
  'hindi/urdu': 'hi',
  'bollywood': 'hi',
  'kollywood': 'ta',
  'tollywood': 'te',
  'mollywood': 'ml',
  'sandalwood': 'kn',
  'hollywood': 'en',
  
  // Common misspellings
  'malyalam': 'ml',
  'malaylam': 'ml',
  'telgu': 'te',
  'kannda': 'kn'
};

// Common genre-language combinations
export const GENRE_LANGUAGE_HINTS: { [key: string]: { language: string; genre: string } } = {
  'bollywood': { language: 'hi', genre: 'musical' },
  'kollywood': { language: 'ta', genre: 'action' },
  'tollywood': { language: 'te', genre: 'drama' },
  'mollywood': { language: 'ml', genre: 'drama' },
  'nollywood': { language: 'en', genre: 'drama' }, // Nigerian cinema
  'anime': { language: 'ja', genre: 'animation' }
};

export class LanguageDetector {
  /**
   * Detect languages mentioned in a search query
   */
  static detectLanguages(query: string): string[] {
    const lowerQuery = query.toLowerCase().trim();
    const detectedLanguages: string[] = [];
    
    // Check for exact language matches
    for (const [language, code] of Object.entries(LANGUAGE_MAPPINGS)) {
      if (lowerQuery.includes(language.toLowerCase())) {
        if (!detectedLanguages.includes(code)) {
          detectedLanguages.push(code);
        }
      }
    }
    
    // Check for genre-language combinations
    for (const [hint, config] of Object.entries(GENRE_LANGUAGE_HINTS)) {
      if (lowerQuery.includes(hint.toLowerCase())) {
        if (!detectedLanguages.includes(config.language)) {
          detectedLanguages.push(config.language);
        }
      }
    }
    
    return detectedLanguages;
  }
  
  /**
   * Map language name to ISO code
   */
  static mapLanguageToCode(language: string): string | null {
    const lowerLanguage = language.toLowerCase().trim();
    return LANGUAGE_MAPPINGS[lowerLanguage] || null;
  }
  
  /**
   * Get language name from code
   */
  static mapCodeToLanguage(code: string): string | null {
    for (const [language, langCode] of Object.entries(LANGUAGE_MAPPINGS)) {
      if (langCode === code) {
        return language.charAt(0).toUpperCase() + language.slice(1);
      }
    }
    return null;
  }
  
  /**
   * Extract non-language terms from query
   */
  static extractNonLanguageTerms(query: string): string[] {
    const lowerQuery = query.toLowerCase().trim();
    const words = lowerQuery.split(/\s+/);
    
    const nonLanguageWords = words.filter(word => {
      // Skip if it's a language
      if (LANGUAGE_MAPPINGS[word]) return false;
      
      // Skip if it's part of a genre-language hint
      for (const hint of Object.keys(GENRE_LANGUAGE_HINTS)) {
        if (hint.toLowerCase().includes(word)) return false;
      }
      
      // Skip common stop words
      const stopWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'movie', 'movies', 'film', 'films'];
      if (stopWords.includes(word)) return false;
      
      return word.length > 1; // Keep meaningful words
    });
    
    return nonLanguageWords;
  }
  
  /**
   * Analyze query for language and content intent
   */
  static analyzeQueryIntent(query: string): {
    languages: string[];
    nonLanguageTerms: string[];
    movieTitle?: string;
    isLanguageSpecific: boolean;
    confidence: number;
  } {
    const languages = this.detectLanguages(query);
    const nonLanguageTerms = this.extractNonLanguageTerms(query);
    
    // Determine if this is a language-specific search
    const isLanguageSpecific = languages.length > 0;
    
    // Try to identify potential movie title
    let movieTitle: string | undefined;
    if (nonLanguageTerms.length > 0) {
      // If we have non-language terms, the first few might be the movie title
      movieTitle = nonLanguageTerms.slice(0, Math.min(3, nonLanguageTerms.length)).join(' ');
    }
    
    // Calculate confidence based on clarity of language detection
    let confidence = 0.5; // Base confidence
    
    if (languages.length === 1) {
      confidence += 0.3; // Single language is clearer
    } else if (languages.length > 1) {
      confidence += 0.1; // Multiple languages reduce confidence slightly
    }
    
    if (nonLanguageTerms.length > 0) {
      confidence += 0.2; // Having content terms increases confidence
    }
    
    // Check for exact language matches (not just partial)
    const queryWords = query.toLowerCase().split(/\s+/);
    for (const word of queryWords) {
      if (LANGUAGE_MAPPINGS[word]) {
        confidence += 0.1; // Exact matches boost confidence
      }
    }
    
    confidence = Math.min(1.0, confidence); // Cap at 1.0
    
    return {
      languages,
      nonLanguageTerms,
      movieTitle,
      isLanguageSpecific,
      confidence
    };
  }
  
  /**
   * Get genre hint from language context
   */
  static getGenreHintFromLanguage(query: string): string | null {
    const lowerQuery = query.toLowerCase();
    
    for (const [hint, config] of Object.entries(GENRE_LANGUAGE_HINTS)) {
      if (lowerQuery.includes(hint)) {
        return config.genre;
      }
    }
    
    return null;
  }
  
  /**
   * Generate search suggestions based on detected language
   */
  static generateLanguageSearchSuggestions(query: string): string[] {
    const analysis = this.analyzeQueryIntent(query);
    const suggestions: string[] = [];
    
    if (analysis.isLanguageSpecific && analysis.movieTitle) {
      for (const langCode of analysis.languages) {
        const langName = this.mapCodeToLanguage(langCode);
        if (langName) {
          suggestions.push(`${analysis.movieTitle} ${langName} movie`);
          suggestions.push(`${analysis.movieTitle} ${langName} film`);
          
          // Add genre-specific suggestions if available
          const genreHint = this.getGenreHintFromLanguage(query);
          if (genreHint) {
            suggestions.push(`${analysis.movieTitle} ${langName} ${genreHint}`);
          }
        }
      }
    }
    
    return suggestions.slice(0, 5); // Limit suggestions
  }
}

export default LanguageDetector;